import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wrench, Image as ImageIcon, CheckCircle, Warning, X, Camera, IdentificationBadge, QrCode } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const conditionMeta = {
  neuf: { label: 'Neuf', color: '#2E7D32', bg: '#E8F5E9' },
  bon: { label: 'Bon', color: '#1565C0', bg: '#E3F2FD' },
  usage: { label: 'Use', color: '#EF6C00', bg: '#FFF3E0' },
  reparation: { label: 'En reparation', color: '#C62828', bg: '#FFEBEE' },
  hors_service: { label: 'Hors service', color: '#424242', bg: '#F5F5F5' },
};

// -----------------------------------------------------------------------------
// Detail Modal — click an equipment card to see all photos + info + signaler
// -----------------------------------------------------------------------------
const EquipmentDetailModal = ({ item, onClose, onOpenReport }) => {
  const [previewIdx, setPreviewIdx] = useState(null);
  if (!item) return null;
  const cm = conditionMeta[item.condition] || { label: item.condition, color: '#5E5F66', bg: '#F4F5F7' };
  const photos = item.photos || [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="eq-detail-modal" style={{ backgroundColor: 'white', borderRadius: 10, width: '100%', maxWidth: 720, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#002FA7 0%,#1565C0 100%)', color: 'white' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>{item.name}</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', opacity: 0.9, textTransform: 'capitalize' }}>
              {item.brand ? `${item.brand} · ` : ''}{item.category}
            </p>
          </div>
          <button onClick={onClose} data-testid="eq-detail-close" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} weight="bold" />
          </button>
        </div>

        <div style={{ overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Photos */}
          <div data-testid="eq-detail-photos">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: '#5E5F66', fontSize: '0.85rem', fontWeight: 600 }}>
              <Camera size={15} /> Photos ({photos.length})
            </div>
            {photos.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9E9E9E', background: '#F4F5F7', borderRadius: 8 }}>
                <ImageSquare36 />
                <p style={{ margin: '6px 0 0', fontSize: '0.82rem' }}>Aucune photo</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                {photos.map((p, idx) => (
                  <button
                    key={p.id || `${p.slot}_${idx}`}
                    onClick={() => setPreviewIdx(idx)}
                    data-testid={`eq-detail-photo-${idx}`}
                    style={{ padding: 0, border: '1px solid #E2E2E6', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: 'transparent', position: 'relative' }}
                  >
                    <img
                      src={`${API_URL}${p.url}`}
                      alt={p.filename || `photo ${idx + 1}`}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                    />
                    {p.comment && (
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px 6px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.comment}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><div style={{ fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Etat</div>
              <span style={{ padding: '2px 10px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600, backgroundColor: cm.bg, color: cm.color }}>{cm.label}</span>
            </div>
            {item.brand && (<div><div style={{ fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Marque</div><div style={{ fontSize: '0.9rem' }}>{item.brand}</div></div>)}
            {item.purchase_date && (<div><div style={{ fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Date d'achat</div><div style={{ fontSize: '0.9rem' }}>{item.purchase_date}</div></div>)}
            <div><div style={{ fontSize: '0.7rem', color: '#9E9E9E', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Categorie</div><div style={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>{item.category}</div></div>
          </div>

          {item.notes && (
            <div style={{ padding: '10px 12px', backgroundColor: '#FFFDE7', borderLeft: '3px solid #FBC02D', fontSize: '0.85rem', color: '#5E5F66', borderRadius: 4 }}>
              <strong>Note :</strong> {item.notes}
            </div>
          )}

          <div style={{ padding: '8px 12px', background: '#E8F5E9', borderRadius: 6, color: '#2E7D32', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} /> Ce materiel est sous votre responsabilite.
          </div>

          <button
            onClick={() => onOpenReport(item)}
            className="btn btn-secondary"
            data-testid={`eq-detail-report-${item.id}`}
            style={{ width: '100%', color: '#C62828', borderColor: '#FFCDD2', backgroundColor: '#FFEBEE' }}
          >
            <Warning size={16} /> Signaler un probleme / dysfonctionnement
          </button>
        </div>

        {/* Lightbox */}
        {previewIdx !== null && photos[previewIdx] && (
          <div onClick={() => setPreviewIdx(null)} data-testid="eq-detail-lightbox" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100, cursor: 'zoom-out' }}>
            <img src={`${API_URL}${photos[previewIdx].url}`} alt="zoom" style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// Tiny empty-state icon (re-use Phosphor)
const ImageSquare36 = () => <ImageIcon size={36} style={{ opacity: 0.45 }} />;

// -----------------------------------------------------------------------------
const MyEquipmentPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);
  const [reportItem, setReportItem] = useState(null);
  const [reportForm, setReportForm] = useState({ issue_type: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const downloadMyBadge = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API_URL}/api/users/${user.id}/badge`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur badge'); }
  };

  const fetchMine = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment/mine`, { withCredentials: true });
      setItems(res.data || []);
    } catch { toast.error('Erreur chargement materiel'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  // Deep link: ?open={equipment_id} from QR code scan -> auto-open detail
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || items.length === 0) return;
    const target = items.find(i => i.id === openId);
    if (target) {
      setDetailItem(target);
      // Strip the param from URL once consumed
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const openReport = (item) => {
    setReportItem(item);
    setReportForm({ issue_type: '', description: '' });
  };
  const submitReport = async () => {
    if (!reportForm.issue_type) return toast.error('Choisir le type de probleme');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/equipment/mine/${reportItem.id}/report`, reportForm, { withCredentials: true });
      toast.success('Signalement envoye - l\'administration est prevenue');
      setReportItem(null);
      setDetailItem(null);
      fetchMine();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="my-equipment-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Wrench size={26} weight="bold" /> Mon materiel
        </h1>
        <p className="page-subtitle">{items.length} element(s) mis a votre disposition - cliquez pour consulter</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
          <button onClick={() => navigate('/scan-materiel')} className="btn btn-primary btn-sm" data-testid="goto-scan-btn">
            <QrCode size={14} /> Scan entrees / sorties
          </button>
          <button onClick={downloadMyBadge} className="btn btn-secondary btn-sm" data-testid="my-badge-btn">
            <IdentificationBadge size={14} /> Ma carte salarie (QR)
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
          <Wrench size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <p style={{ margin: 0 }}>Aucun materiel ne vous est actuellement affecte.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
          {items.map(item => {
            const cm = conditionMeta[item.condition] || { label: item.condition, color: '#5E5F66', bg: '#F4F5F7' };
            const primaryPhoto = item.photos?.[0];
            const photosCount = (item.photos || []).length;
            return (
              <button
                key={item.id}
                onClick={() => setDetailItem(item)}
                data-testid={`my-eq-${item.id}`}
                className="card"
                style={{ overflow: 'hidden', textAlign: 'left', padding: 0, border: '1px solid #E2E2E6', background: 'white', cursor: 'pointer', transition: 'transform 120ms ease, box-shadow 120ms ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                {/* Cover photo */}
                <div style={{ position: 'relative' }}>
                  {primaryPhoto ? (
                    <img
                      src={`${API_URL}${primaryPhoto.url}?t=${item.id}`}
                      alt={item.name}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block', background: '#F4F5F7' }}
                    />
                  ) : (
                    <div style={{ height: 120, backgroundColor: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9E9E9E' }}>
                      <ImageIcon size={40} />
                    </div>
                  )}
                  {photosCount > 1 && (
                    <span style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.65)', color: 'white', padding: '2px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Camera size={11} weight="bold" /> {photosCount}
                    </span>
                  )}
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 6 }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</h3>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, backgroundColor: cm.bg, color: cm.color, whiteSpace: 'nowrap' }}>{cm.label}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginBottom: 4, textTransform: 'capitalize' }}>
                    {item.brand && <span>{item.brand} · </span>}{item.category}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#1D4ED8', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    Voir le detail →
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <EquipmentDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onOpenReport={openReport}
      />

      {reportItem && (
        <div onClick={() => !submitting && setReportItem(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2200, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '500px', overflow: 'hidden' }} data-testid="report-modal">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FFEBEE' }}>
              <h3 style={{ margin: 0, color: '#C62828', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Warning size={20} /> Signaler un souci
              </h3>
              <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setReportItem(null)}><X size={14} /></button>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ margin: '0 0 16px', fontSize: '0.9rem' }}>Materiel : <strong>{reportItem.name}</strong></p>
              <div className="form-group">
                <label className="form-label">Type de probleme *</label>
                <Select value={reportForm.issue_type} onValueChange={(v) => setReportForm({ ...reportForm, issue_type: v })}>
                  <SelectTrigger data-testid="report-issue-type"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent className="z-[9999]">
                    <SelectItem value="casse">Casse / Dommage</SelectItem>
                    <SelectItem value="vol">Vol / Perte</SelectItem>
                    <SelectItem value="reparation">Reparation necessaire</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input" rows={4}
                  placeholder="Decrivez le probleme rencontre..."
                  value={reportForm.description}
                  onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                  data-testid="report-description"
                />
              </div>
              <div style={{ padding: '10px 12px', backgroundColor: '#FFF3E0', borderRadius: '4px', fontSize: '0.8rem', color: '#E65100', marginBottom: '12px' }}>
                L'administrateur sera immediatement notifie. Le statut du materiel sera mis a jour automatiquement.
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setReportItem(null)} disabled={submitting} style={{ flex: 1 }}>Annuler</button>
                <button className="btn btn-primary" onClick={submitReport} disabled={submitting} style={{ flex: 1, backgroundColor: '#C62828' }} data-testid="report-submit">
                  {submitting ? 'Envoi...' : 'Envoyer signalement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyEquipmentPage;
